
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, DollarSign } from "lucide-react";

interface DashboardStatsProps {
  stats: {
    totalOrders: number;
    totalRevenue: number;
    totalRevenueByCurrency: Record<string, number>;
    totalProducts: number;
    totalSales: number;
  };
  isLoading: boolean;
  isConnected: boolean;
  formatRevenue: (amount: number, currency: string) => string;
}

const DashboardStats = ({ stats, isLoading, isConnected, formatRevenue }: DashboardStatsProps) => {
  // Get the primary currency for display (most common currency or first one)
  const primaryCurrency = Object.keys(stats.totalRevenueByCurrency).length > 0 
    ? Object.keys(stats.totalRevenueByCurrency)[0] 
    : 'USD';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
          <Package className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : stats.totalOrders}
          </div>
          <p className="text-xs text-muted-foreground">
            {isConnected ? "From your Etsy shop" : "Connect Etsy to see data"}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Revenue</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : formatRevenue(stats.totalRevenueByCurrency[primaryCurrency] || 0, primaryCurrency)}
          </div>
          <p className="text-xs text-muted-foreground">
            {isConnected ? `Total in ${primaryCurrency}` : "Connect Etsy to see data"}
          </p>
          {/* Show additional currencies if there are multiple */}
          {Object.keys(stats.totalRevenueByCurrency).length > 1 && (
            <div className="mt-2 space-y-1">
              {Object.entries(stats.totalRevenueByCurrency)
                .filter(([currency]) => currency !== primaryCurrency)
                .map(([currency, amount]) => (
                  <div key={currency} className="text-xs text-gray-600">
                    {formatRevenue(amount, currency)}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Products</CardTitle>
          <Package className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? "..." : stats.totalProducts}
          </div>
          <p className="text-xs text-muted-foreground">
            {isConnected ? "Active listings" : "Connect Etsy to see data"}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStats;
